<script setup lang="ts">
export type PipelineNodeState = 'complete' | 'not-started' | 'active'

const props = withDefaults(
  defineProps<{
    label?: string
    subtitle?: string
    state?: PipelineNodeState
    badgeCompleted?: number
    badgeTotal?: number
    showExpand?: boolean
    isSite?: boolean
    isJob?: boolean
    isPlaceholder?: boolean
  }>(),
  {
    label: '',
    subtitle: '',
    state: 'not-started',
    badgeCompleted: undefined,
    badgeTotal: undefined,
    showExpand: false,
    isSite: false,
    isJob: false,
    isPlaceholder: false
  }
)

const hasBadge =
  props.badgeCompleted !== undefined && props.badgeTotal !== undefined
</script>

<template>
  <div
    class="pipeline-node"
    :class="[
      `state-${state}`,
      { 'is-site': isSite, 'is-job': isJob, placeholder: isPlaceholder }
    ]"
  >
    <template v-if="!isPlaceholder">
      <div class="node-left">
        <span v-if="showExpand" class="expand-icon" aria-hidden="true">▸</span>
        <div class="node-label-wrap">
          <span class="node-label">{{ label }}</span>
          <span v-if="subtitle" class="node-subtitle">{{ subtitle }}</span>
        </div>
      </div>
      <div class="node-right">
        <span v-if="hasBadge" class="node-badge">{{ badgeCompleted }} / {{ badgeTotal }}</span>
        <span class="tool-icon" aria-hidden="true">⚙</span>
      </div>
    </template>
  </div>
</template>

<style scoped>
.pipeline-node {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 44px;
  padding: 0.45rem 0.6rem;
  border: 2px solid;
  border-color: #d3d3d3 #ebebeb #5a5a5a #5a5a5a;
  background: #f8f8f8;
  color: #111;
  box-sizing: border-box;
}

.pipeline-node.placeholder {
  visibility: hidden;
}

.node-left,
.node-right {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  min-width: 0;
}

.node-label-wrap {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.node-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.88rem;
}

.node-subtitle {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.72rem;
  color: #666;
}

.expand-icon {
  font-size: 0.85rem;
}

.node-badge {
  font-size: 0.75rem;
  border: 1px solid #000;
  background: #fff;
  padding: 0.1rem 0.35rem;
  white-space: nowrap;
}

.tool-icon {
  font-size: 0.8rem;
}

.state-complete {
  background: #e8f6e8;
}

.state-active {
  background: #fff8d6;
}

.state-not-started {
  background: #f6f6f6;
}

.is-site {
  font-weight: 700;
}
</style>
